import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { ASSETS, ASSISTANT_GREETING, ASSISTANT_GREETING_2, ASSISTANT_FALLBACK, ASSISTANT_QA } from "@/data/content";

export default function Assistant() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: "bot", text: ASSISTANT_GREETING },
    { from: "bot", text: ASSISTANT_GREETING_2 },
  ]);
  const [draft, setDraft] = useState("");
  const bodyRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 350);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollTo({ top: bodyRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, open]);

  const push = (q, a) =>
    setMessages((m) => [...m, { from: "user", text: q }, { from: "bot", text: a }]);

  const send = () => {
    const t = draft.trim();
    if (!t) return;
    setDraft("");
    const match = ASSISTANT_QA.find((item) => item.q.toLowerCase() === t.toLowerCase());
    push(t, match ? match.a : ASSISTANT_FALLBACK);
  };

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60]" data-testid="assistant-root">
      <AnimatePresence>
        {visible && !open && (
          <motion.button
            key="bubble"
            type="button"
            initial={{ opacity: 0, scale: 0.5, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 30 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            onClick={() => setOpen(true)}
            className="relative block w-14 h-14 sm:w-16 sm:h-16 rounded-full cursor-pointer ring-2 ring-amber-300/80 shadow-[0_10px_35px_rgba(0,0,0,0.55),0_0_25px_rgba(230,184,92,0.35)] hover:scale-105 transition-transform duration-300"
            data-testid="assistant-toggle"
            aria-label="Швидкий помічник"
          >
            <img
              src={ASSETS.giraffe}
              alt="Помічник — жираф"
              className="w-full h-full rounded-full object-cover bg-white"
              loading="lazy"
              decoding="async"
            />
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] border-2 border-[#061812] animate-glow-pulse" />
          </motion.button>
        )}

        {visible && open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.85, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 40 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="w-[min(94vw,24rem)] rounded-3xl glass overflow-hidden shadow-[0_25px_70px_rgba(0,0,0,0.6)] flex flex-col"
            data-testid="assistant-panel"
            data-lenis-prevent
          >
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-amber-400/15 bg-white/[0.03]">
              <img
                src={ASSETS.giraffe}
                alt="Помічник — жираф"
                className="w-10 h-10 rounded-full object-cover bg-white ring-1 ring-amber-300/50 shrink-0"
                loading="lazy"
                decoding="async"
              />
              <div className="min-w-0 flex-1">
                <div className="font-cinzel text-amber-200 text-sm tracking-[0.14em]" data-testid="assistant-title">
                  ШВИДКИЙ ПОМІЧНИК
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-amber-100/80 hover:text-amber-300 hover:bg-white/10 transition-colors cursor-pointer shrink-0"
                data-testid="assistant-close"
                aria-label="Закрити помічника"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div
              ref={bodyRef}
              className="px-4 py-3 space-y-2.5 max-h-[46vh] sm:max-h-[52vh] overflow-y-auto overscroll-contain magical-scrollbar"
              data-testid="assistant-messages"
              data-lenis-prevent
            >
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                      m.from === "user"
                        ? "bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] font-medium rounded-br-md"
                        : "bg-white/[0.06] border border-white/10 text-slate-100/95 rounded-bl-md"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4 pb-2 pt-1 max-h-28 overflow-y-auto overscroll-contain magical-scrollbar flex flex-wrap gap-1.5" data-testid="assistant-questions" data-lenis-prevent>
              {ASSISTANT_QA.map((item, i) => (
                <button
                  key={item.q}
                  type="button"
                  onClick={() => push(item.q, item.a)}
                  className="text-left text-[11px] rounded-full border border-amber-300/30 bg-amber-300/8 px-3 py-1.5 text-amber-100/90 hover:bg-amber-300/20 hover:border-amber-300/60 transition-colors cursor-pointer"
                  data-testid={`assistant-question-${i}`}
                >
                  {item.q}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 px-3.5 py-3 border-t border-amber-400/15">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Напишіть своє питання…"
                className="flex-1 min-w-0 rounded-full bg-white/[0.05] border border-amber-400/20 px-4 py-2.5 text-sm text-white placeholder:text-slate-400/70 outline-none focus:border-amber-300/60 transition-colors"
                data-testid="assistant-input"
              />
              <button
                type="button"
                onClick={send}
                className="w-10 h-10 rounded-full bg-gradient-to-r from-[#f3d079] to-[#d9a53a] text-[#061812] flex items-center justify-center hover:shadow-[0_0_20px_rgba(230,184,92,0.6)] transition-shadow cursor-pointer shrink-0"
                data-testid="assistant-send"
                aria-label="Надіслати"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
